import * as React from "react";
import { useCallback, useContext, useMemo, useState } from "react";
import { useField } from "formik";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { SortableList, FormikInput, InputDescription, Link } from "components/common";
import IconButton from "components/common/IconButton";
import NoEntitiesExist from "components/common/NoEntitiesExist";
import defaultTitle from "views/components/defaultTitle";
import WidgetDetailsContext from "report/report-creation/WidgetDetailsContext";
import { Button } from "components/bootstrap";
import WidgetTitle from "report/common/WidgetTitle";
import Routes from "routing/Routes";
import usePermissions from "hooks/usePermissions";
const WidgetListItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
const WidgetInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: top;
`;
const WidgetTitleWrapper = styled.div`
  margin-top: 1px;
`;
const WidgetActions = styled.div`
  display: flex;
`;
const WidgetDescription = ({ onRemove, id, dashboardId, index, disabled }) => {
  var _a;
  const { t } = useTranslation("enterprise_report");
  const { widgets, isLoading } = useContext(WidgetDetailsContext);
  const { isPermitted } = usePermissions();
  const [, { error }] = useField(`widgets.${index}`);
  const widgetDetails = useMemo(() => widgets[id], [widgets, id]);
  const [showDetailsConfiguration, setShowDetailsConfiguration] = useState(false);
  const _onRemove = useCallback(() => {
    if (window.confirm(t("widgets_list.confirm_delete"))) {
      onRemove(id, dashboardId);
    }
  }, [dashboardId, id, onRemove, t]);
  const toggleEditModal = () => {
    setShowDetailsConfiguration((cur) => !cur);
  };
  const [, { value: customTitle }] = useField(`widgets.${index}.title`);
  const detailsAreAvailable = !isLoading && !!widgetDetails;
  const widgetTitle = (_a = widgetDetails == null ? void 0 : widgetDetails.description) != null ? _a : widgetDetails ? defaultTitle(widgetDetails) : "";
  const title = !(customTitle == null ? void 0 : customTitle.trim()) ? widgetTitle : customTitle;
  const isEditPermitted = isPermitted(`view:edit:${dashboardId}`);
  return /* @__PURE__ */ React.createElement(WidgetListItem, null, /* @__PURE__ */ React.createElement(WidgetInfo, null, /* @__PURE__ */ React.createElement(WidgetTitleWrapper, null, /* @__PURE__ */ React.createElement(
    WidgetTitle,
    {
      title,
      isLoading: isLoading && !widgetDetails,
      detailsAreAvailable
    }
  )), /* @__PURE__ */ React.createElement(WidgetActions, null, /* @__PURE__ */ React.createElement(
    IconButton,
    {
      title: t("widgets_list.edit_description"),
      name: "edit_square",
      onClick: toggleEditModal,
      disabled: !detailsAreAvailable || disabled
    }
  ), isEditPermitted && /* @__PURE__ */ React.createElement(Link, { to: `${Routes.dashboard_show(dashboardId)}?focusedId=${id}&editing=true`, target: "_blank" }, /* @__PURE__ */ React.createElement(IconButton, { name: "checkbook", title: t("widget_row.edit_widget") })), /* @__PURE__ */ React.createElement(IconButton, { title: t("widgets_list.remove_widget"), name: "delete", onClick: _onRemove, disabled }))), error && /* @__PURE__ */ React.createElement(InputDescription, { error, className: "no-bm" }), showDetailsConfiguration && /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      name: `widgets.${index}.title`,
      id: `widgets.${index}.title`,
      label: t("widgets_list.custom_title"),
      placeholder: widgetTitle
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      name: `widgets.${index}.description`,
      id: `widgets.${index}.description`,
      label: t("widgets_list.custom_description"),
      type: "textarea"
    }
  ), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      id: `widgets.${index}.height`,
      label: t("widgets_list.custom_height"),
      name: `widgets.${index}.height`,
      help: t("widgets_list.custom_height_help"),
      min: 1,
      max: 12
    }
  ), /* @__PURE__ */ React.createElement(Button, { onClick: toggleEditModal, bsSize: "small" }, t("widgets_list.hide"))));
};
const WidgetsList = ({ disabled }) => {
  const { t } = useTranslation("enterprise_report");
  const [{ value: widgets }, , { setValue: setWidgets }] = useField("widgets");
  const [, , { setValue: setPositions }] = useField("positions");
  const onRemove = useCallback(
    (widgetId, dashboardId) => setWidgets(widgets.filter((widget) => widget.widgetId !== widgetId || widget.dashboardId !== dashboardId)),
    [setWidgets, widgets]
  );
  const onMoveItem = useCallback(
    (newWidgetItems) => {
      const newWidgets = newWidgetItems.map((widgetItem) => {
        const [dashboardId, widgetId] = widgetItem.id.split("/");
        return widgets.find((widget) => widget.widgetId === widgetId && widget.dashboardId === dashboardId);
      });
      const newPositions = newWidgets.map((widget, idx) => ({
        row: idx + 1,
        col: 1,
        dashboard_widget_id: widget.widgetId
      }));
      setPositions(newPositions);
      return setWidgets(newWidgets);
    },
    [setPositions, setWidgets, widgets]
  );
  const listItems = useMemo(
    () => widgets.map(({ widgetId, dashboardId }, index) => ({
      id: `${dashboardId}/${widgetId}`,
      title: /* @__PURE__ */ React.createElement(
        WidgetDescription,
        {
          id: widgetId,
          index,
          dashboardId,
          onRemove,
          disabled
        }
      )
    })),
    [onRemove, widgets, disabled]
  );
  return listItems.length > 0 ? /* @__PURE__ */ React.createElement(SortableList, { items: listItems, onMoveItem, disableDragging: disabled }) : /* @__PURE__ */ React.createElement(NoEntitiesExist, null, t("widgets_list.no_widgets"));
};
export default WidgetsList;
